import type { NextPage } from 'next'
import { useRouter } from 'next/router'
import { Schemas } from '~/api/gen'
import { Divider } from '~/components/elements/Divider'
import { NoResults } from '~/components/elements/NoResults'
import { Pagination } from '~/components/modules/Pagination/Pagination'
import { usePaginationProps } from '~/hooks/usePaginationProps'
import { FrequentlyUsedCoordinateItems } from '../shared/memberDetail/components/FrequentlyUsedCoordinateItems'
import { FrequentlyUsedTags } from '../shared/memberDetail/components/FrequentlyUsedTags'
import { MemberDetailLayout } from '../shared/memberDetail/components/MemberDetailLayout'
import { getVideoThumbnailAlt } from '../shared/video/lib/getVideoThumbnailAlt'
import { VideoCard } from './components/VideoCard'
import { convertToItemProps } from './lib/convertToItemProps'

export const FETCH_COUNT_PER_PAGE = 60

export type Props = {
  member: Schemas.member_large_detail
  videoCollection: Schemas.get_web_members_user_name_ways_v1_response
}

export const MemberVideoList: NextPage<Props> = ({
  member,
  videoCollection,
}) => {
  const router = useRouter()
  const currentPage = Number(router.query.pageno) || 1
  const isFirstPage = currentPage === 1
  const frequentlyUsedTagsExist = member.frequently_used_tags.length > 0
  const frequentlyUsedCoordinateItemsExist =
    member.frequently_used_coordinate_items.length > 0

  const paginationProps = usePaginationProps(
    currentPage,
    Math.ceil(videoCollection.total_count / FETCH_COUNT_PER_PAGE),
    false
  )

  const isNoResults = videoCollection.total_count === 0

  return (
    <MemberDetailLayout
      pageType="videos"
      member={member}
      currentPage={currentPage}
    >
      {!isNoResults ? (
        <>
          <div
            className={`grid grid-cols-3 gap-[1px] xl:grid-cols-4 xl:gap-5 ${isFirstPage ? '' : 'xl:pt-5'}`}
          >
            {videoCollection.ways.map((way, i) => {
              if (way.coordinate_items[0] === undefined) return null
              return (
                <VideoCard
                  key={way.id}
                  imageUrl={way.member_specified_thumbnail_url.url_270}
                  imageAlt={getVideoThumbnailAlt(member.nick_name, way.title, {
                    brandName: way.coordinate_items[0]?.brand?.name,
                    categoryName: way.coordinate_items[0]?.item_category?.name,
                    childCategoryName:
                      way.coordinate_items[0]?.item_child_category?.name,
                  })}
                  publishedAt={way.published_at ?? ''}
                  itemProps={convertToItemProps(way.coordinate_items[0])}
                  href={`/${member.user_name}/videos/${way.id}/`}
                  imagePriority={i < 6}
                  // PCのとき1枚目、SPのとき2枚目がLCP
                  imageFetchPriority={i === 0 || i === 1}
                />
              )
            })}
          </div>
          <div className="pt-5">
            <Pagination {...paginationProps} />
          </div>
          {(frequentlyUsedTagsExist || frequentlyUsedCoordinateItemsExist) && (
            <>
              <div
                className={`hidden xl:block ${isNoResults ? 'xl:pt-[70px]' : 'xl:pt-7'}`}
              >
                <Divider />
              </div>
              <section className="grid gap-y-[22px] px-4 pt-10 xl:gap-y-7 xl:px-0 xl:pb-[60px] xl:pt-[30px]">
                {frequentlyUsedTagsExist && (
                  <FrequentlyUsedTags
                    nickName={member.nick_name}
                    frequentlyUsedTags={member.frequently_used_tags}
                  />
                )}
                {frequentlyUsedCoordinateItemsExist && (
                  <FrequentlyUsedCoordinateItems
                    nickName={member.nick_name}
                    frequentlyUsedCoordinateItems={
                      member.frequently_used_coordinate_items
                    }
                  />
                )}
              </section>
            </>
          )}
        </>
      ) : (
        <NoResults
          className="pb-[38px] pt-[33px] xl:pb-[90px] xl:pt-9"
          type="COORDINATE"
        >
          まだ投稿はありません
        </NoResults>
      )}
    </MemberDetailLayout>
  )
}
